// src/components/dashboard/VehicleStatus.jsx
import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';

export default function VehLocDialog({ open, onClose, selectedStat }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const data = useMemo(() => {
    return selectedStat?.dataObj?.vehicles || [];
  }, [selectedStat]);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setPage(0);
    }
  }, [open, selectedStat]);

  const { processedData } = useMemo(() => {
    const formattedRows = (data || []).map((item, index) => {
      let condition = 'Unknown';
      let colorChip = 'default';

      const statusRaw = String(item.raw.status || '').toLowerCase();
      const engineRaw = String(item.raw.engine || '').toUpperCase();
      const speed = Number(item.raw.speed || 0);
      const isLoaded = item.raw.isLoaded || false;
      const rowData = item.raw;

      const isAtPolygon =
        statusRaw.includes('at pool') ||
        statusRaw.includes('at geofence') ||
        statusRaw.includes('at customer') ||
        statusRaw.includes('at polygon');
      const isOverSpeedSpatial = rowData.spatial_id > 0 && rowData.spatial_max_speed > 0 && speed > rowData.spatial_max_speed && isLoaded;
      const isUnderSpeedSpatial = rowData.spatial_id > 0 && rowData.spatial_min_speed > 0 && speed < rowData.spatial_min_speed && isLoaded;
      const isStatusOverSpeed = statusRaw.includes('over speed') || statusRaw.includes('overspeed');
      const isStatusUnderSpeed = statusRaw.includes('under speed') || statusRaw.includes('underspeed');
      const gpsRaw = String(item.gps || item.raw?.gps || '').toLowerCase();
      const isGpsUpdated = gpsRaw.includes('updated') || gpsRaw.includes('update');

      if ((isStatusOverSpeed || isOverSpeedSpatial || isStatusUnderSpeed || isUnderSpeedSpatial) && isGpsUpdated) {
        condition = 'Moving';
        colorChip = 'success';
      } else if (gpsRaw.includes('delay')) {
        if (gpsRaw.includes('24')) condition = 'Delay > 24h';
        else if (gpsRaw.includes('1')) condition = 'Delay > 1h';
        else condition = 'Delay';
        colorChip = 'error';
      } else if (statusRaw.includes('delay')) {
        if (statusRaw.includes('24')) condition = 'Delay > 24h';
        else if (statusRaw.includes('1')) condition = 'Delay > 1h';
        else condition = 'Delay';
        colorChip = 'error';
      } else if (statusRaw.includes('maintenance')) {
        condition = 'Maintenance';
        colorChip = 'error';
      } else if ((engineRaw === 'ON' || engineRaw === 'IDLE') && speed > 0 && isGpsUpdated) {
        condition = 'Moving';
        colorChip = 'success';
      } else if ((engineRaw === 'ON' || engineRaw === 'IDLE') && speed === 0 && isGpsUpdated) {
        condition = 'Idle';
        colorChip = 'warning';
      } else if (engineRaw === 'OFF') {
        if (isAtPolygon) {
          condition = 'Parked';
          colorChip = 'primary';
        } else {
          condition = 'Stopped';
          colorChip = 'default';
        }
      } else if (speed > 0 && isGpsUpdated) {
        condition = 'Moving';
        colorChip = 'success';
      } else if (isAtPolygon) {
        condition = 'Parked';
        colorChip = 'primary';
      } else {
        condition = 'Stopped';
        colorChip = 'default';
      }

      // Format Sensors
      const sensors = item.raw.notif_sensor
        ? Object.entries(item.raw.notif_sensor).map(([k, v]) => {
            const name = k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            return { name, value: String(v) };
          })
        : [];

      return {
        id: item.raw.id || `v-stat-${index}`,
        vehicleNumber: item.raw.vehicle_license_plate || '-',
        bodyName: item.raw.vehicle_name || '-',
        vehicleType: item.raw.vehicle_type_name || '-',
        imei: item.raw.imei_no || '-',
        condition,
        colorChip,
        lastUpdate: item.raw.last_update ? dayjs(item.raw.last_update).format('YYYY-MM-DD HH:mm:ss') : '-',
        gps: item.raw.gps || '-',
        engine: item.raw.engine || '-',
        speed: item.raw.speed !== undefined ? `${item.raw.speed} Km/H` : '-',
        rpm: item?.raw.canbus?.canEngineRPM || '-',
        sensors,
        location: item.raw.location || '-',
        lat: item.raw.lat,
        lon: item.raw.lon,
        currentOdo: item.raw.current_odo !== undefined ? item.raw.current_odo : '-',
        power: item.raw.power_volt || '-',
        position: item.raw.position || '-',
        gsm: item.raw.gsm_signal || '-'
      };
    });

    return { processedData: formattedRows };
  }, [data]);

  // Filter based on search term
  const filteredRows = useMemo(() => {
    let result = processedData;

    if (!searchTerm) return result;

    const lowerSearch = searchTerm.toLowerCase();

    return result.filter((row) => {
      const searchString = [
        row.vehicleNumber,
        row.bodyName,
        row.vehicleType,
        row.imei,
        row.condition,
        row.lastUpdate,
        row.gps,
        row.engine,
        row.speed,
        row.rpm,
        row.location,
        row.currentOdo,
        row.power,
        row.position,
        row.gsm,
        ...row.sensors.map((s) => `${s.name} ${s.value}`)
      ]
        .join(' ')
        .toLowerCase();

      return searchString.includes(lowerSearch);
    });
  }, [processedData, searchTerm]);

  // Paginate filtered rows
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  // Handle Export to Excel
  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Vehicle Status');

    worksheet.columns = [
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 20 },
      { width: 15 },
      { width: 25 },
      { width: 10 },
      { width: 10 },
      { width: 15 },
      { width: 10 },
      { width: 20 },
      { width: 25 },
      { width: 40 },
      { width: 15 },
      { width: 10 },
      { width: 10 },
      { width: 10 }
    ];

    const headers = [
      { cell: 'A1', val: 'Vehicle', merge: 'A1:A2' },
      { cell: 'B1', val: 'Body Name', merge: 'B1:B2' },
      { cell: 'C1', val: 'Vehicle Type', merge: 'C1:C2' },
      { cell: 'D1', val: 'Imei', merge: 'D1:D2' },
      { cell: 'E1', val: 'Condition', merge: 'E1:E2' },
      { cell: 'F1', val: 'Last Update', merge: 'F1:F2' },
      { cell: 'G1', val: 'Device Status', merge: 'G1:L1' },
      { cell: 'M1', val: 'Location', merge: 'M1:M2' },
      { cell: 'N1', val: 'Abnormality Status', merge: 'N1:Q1' }
    ];

    const subHeaders = [
      { cell: 'G2', val: 'Gps' },
      { cell: 'H2', val: 'Engine' },
      { cell: 'I2', val: 'Speed' },
      { cell: 'J2', val: 'Rpm' },
      { cell: 'K2', val: 'Sensor' },
      { cell: 'L2', val: 'Sensor Value' },
      { cell: 'N2', val: 'Current Odo' },
      { cell: 'O2', val: 'Power' },
      { cell: 'P2', val: 'Position' },
      { cell: 'Q2', val: 'Gsm' }
    ];

    headers.forEach((h) => {
      worksheet.mergeCells(h.merge);
      worksheet.getCell(h.cell).value = h.val;
    });

    subHeaders.forEach((h) => {
      worksheet.getCell(h.cell).value = h.val;
    });

    // Style Headers
    const allHeaderCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'M1', 'N1', ...subHeaders.map((s) => s.cell)];
    allHeaderCells.forEach((cell) => {
      const c = worksheet.getCell(cell);
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF485D9A' } };
      c.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      c.alignment = { vertical: 'middle', horizontal: 'center' };
      c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    let currentRow = 3;

    filteredRows.forEach((row) => {
      const sensors = row.sensors.length > 0 ? row.sensors : [{ name: '-', value: '-' }];
      const span = sensors.length;

      worksheet.getCell(`A${currentRow}`).value = row.vehicleNumber;
      worksheet.getCell(`B${currentRow}`).value = row.bodyName;
      worksheet.getCell(`C${currentRow}`).value = row.vehicleType;
      worksheet.getCell(`D${currentRow}`).value = row.imei;
      worksheet.getCell(`E${currentRow}`).value = row.condition;
      worksheet.getCell(`F${currentRow}`).value = row.lastUpdate;

      worksheet.getCell(`G${currentRow}`).value = row.gps;
      worksheet.getCell(`H${currentRow}`).value = row.engine;
      worksheet.getCell(`I${currentRow}`).value = row.speed;
      worksheet.getCell(`J${currentRow}`).value = row.rpm;
      worksheet.getCell(`K${currentRow}`).value = sensors[0].name;
      worksheet.getCell(`L${currentRow}`).value = sensors[0].value;

      worksheet.getCell(`M${currentRow}`).value = row.location;
      worksheet.getCell(`N${currentRow}`).value = row.currentOdo;
      worksheet.getCell(`O${currentRow}`).value = row.power;
      worksheet.getCell(`P${currentRow}`).value = row.position;
      worksheet.getCell(`Q${currentRow}`).value = row.gsm;

      // Apply borders to first row
      const columns = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q'];
      columns.forEach((col) => {
        const c = worksheet.getCell(`${col}${currentRow}`);
        c.alignment = { vertical: 'middle', horizontal: 'center' };
        c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      // Subsequent sensor rows
      for (let i = 1; i < span; i++) {
        const sr = currentRow + i;
        worksheet.getCell(`K${sr}`).value = sensors[i].name;
        worksheet.getCell(`L${sr}`).value = sensors[i].value;
        ['K', 'L'].forEach((col) => {
          const c = worksheet.getCell(`${col}${sr}`);
          c.alignment = { vertical: 'middle', horizontal: 'center' };
          c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
      }

      if (span > 1) {
        ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'M', 'N', 'O', 'P', 'Q'].forEach((col) => {
          worksheet.mergeCells(`${col}${currentRow}:${col}${currentRow + span - 1}`);
        });
      }

      currentRow += span;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Vehicle_Status.xlsx';
    link.click();
  };

  const headerStyle = {
    backgroundColor: '#485D9A',
    color: 'white',
    border: '1px solid #ddd',
    padding: '12px 8px',
    textAlign: 'center',
    fontWeight: 'bold',
    whiteSpace: 'nowrap'
  };

  const headerStyleLoc = {
    ...headerStyle,
    minWidth: '300px'
  };

  const headerStyleParent = {
    ...headerStyle,
    minWidth: '150px'
  };

  const cellStyle = {
    border: '1px solid #ddd',
    padding: '8px',
    textAlign: 'center',
    fontSize: '0.8rem'
  };
  const cellStyleLeft = {
    border: '1px solid #ddd',
    padding: '8px',
    textAlign: 'left',
    fontSize: '0.8rem'
  };
  const cellStyleLeftSmall = {
    border: '1px solid #ddd',
    padding: '8px',
    textAlign: 'left',
    fontSize: '0.7rem'
  };

  const getStickyStyle = (leftPos, z, bgColor) => ({
    position: 'sticky',
    left: leftPos,
    zIndex: z,
    backgroundColor: bgColor
  });

  const getAbnormalityChipColor = (val, goodValues = ['Normal', 'Good']) => {
    if (!val || val === '-') return 'default';
    return goodValues.includes(val) ? 'info' : 'error';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography fontWeight="bold">Vehicles at {selectedStat?.title}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2, bgcolor: '#f4f6f8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search all columns..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            sx={{ bgcolor: '#485D9A', '&:hover': { bgcolor: '#3b4c80' } }}
          >
            Export Excel
          </Button>
        </Box>

        {/* Custom Table */}
        <Box
          sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '60vh', border: '1px solid #ddd', borderRadius: 1, bgcolor: '#ffffff' }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1500 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <th
                  rowSpan={2}
                  style={{
                    ...headerStyleParent,
                    ...getStickyStyle(0, 4, '#485D9A'),
                    minWidth: 150,
                    maxWidth: 150,
                    boxSizing: 'border-box'
                  }}
                >
                  Vehicle
                </th>
                <th
                  rowSpan={2}
                  style={{
                    ...headerStyleParent,
                    ...getStickyStyle(150, 4, '#485D9A'),
                    minWidth: 150,
                    maxWidth: 150,
                    boxSizing: 'border-box'
                  }}
                >
                  Vehicle Type
                </th>
                <th
                  rowSpan={2}
                  style={{
                    ...headerStyleParent,
                    ...getStickyStyle(300, 4, '#485D9A'),
                    minWidth: 150,
                    maxWidth: 150,
                    boxSizing: 'border-box'
                  }}
                >
                  Imei
                </th>
                <th rowSpan={2} style={headerStyle}>
                  Condition
                </th>
                <th rowSpan={2} style={headerStyleParent}>
                  Last Update
                </th>
                <th colSpan={6} style={headerStyle}>
                  Device Status
                </th>
                <th rowSpan={2} style={headerStyleLoc}>
                  Location
                </th>
                <th colSpan={4} style={headerStyle}>
                  Abnormality Status
                </th>
              </tr>
              <tr>
                <th style={headerStyle}>Gps</th>
                <th style={headerStyle}>Engine</th>
                <th style={headerStyle}>Speed</th>
                <th style={headerStyle}>Rpm</th>
                <th style={headerStyleParent}>Sensor</th>
                <th style={headerStyleParent}>Sensor Value</th>
                <th style={headerStyle}>Current Odo</th>
                <th style={headerStyle}>Power</th>
                <th style={headerStyle}>Position</th>
                <th style={headerStyle}>Gsm</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={17} style={{ ...cellStyle, padding: '20px' }}>
                    No data found
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row) => {
                  const sensors = row.sensors.length > 0 ? row.sensors : [{ name: '-', value: '-' }];
                  const rowSpan = sensors.length;

                  return (
                    <React.Fragment key={row.id}>
                      <tr>
                        <td
                          rowSpan={rowSpan}
                          style={{
                            ...cellStyleLeft,
                            ...getStickyStyle(0, 1, '#fff'),
                            minWidth: 150,
                            maxWidth: 150,
                            boxSizing: 'border-box'
                          }}
                        >
                          {row.vehicleNumber}
                        </td>
                        <td
                          rowSpan={rowSpan}
                          style={{
                            ...cellStyleLeft,
                            ...getStickyStyle(150, 1, '#fff'),
                            minWidth: 150,
                            maxWidth: 150,
                            boxSizing: 'border-box'
                          }}
                        >
                          {row.vehicleType}
                        </td>
                        <td
                          rowSpan={rowSpan}
                          style={{
                            ...cellStyleLeft,
                            ...getStickyStyle(300, 1, '#fff'),
                            minWidth: 150,
                            maxWidth: 150,
                            boxSizing: 'border-box'
                          }}
                        >
                          {row.imei}
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          <Chip label={row.condition} color={row.colorChip} size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          <Chip
                            label={row.lastUpdate}
                            color={String(row.gps).toLowerCase() === 'update' ? 'primary' : 'error'}
                            variant="outlined"
                            size="small"
                          />
                        </td>

                        <td rowSpan={rowSpan} style={cellStyle}>
                          {row.gps}
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          {row.engine}
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          {row.speed}
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          {row.rpm}
                        </td>
                        <td style={cellStyleLeft}>{sensors[0].name}</td>
                        <td style={cellStyleLeftSmall}>{sensors[0].value}</td>

                        <td rowSpan={rowSpan} style={cellStyleLeft}>
                          {row.lat && row.lon ? (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${row.lat},${row.lon}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#1976d2', textDecoration: 'none' }}
                            >
                              {row.location}
                            </a>
                          ) : (
                            row.location
                          )}
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          {row.currentOdo}
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          <Chip label={row.power} color={getAbnormalityChipColor(row.power, ['Normal'])} size="small" />
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          <Chip label={row.position} color={getAbnormalityChipColor(row.position, ['Good'])} size="small" />
                        </td>
                        <td rowSpan={rowSpan} style={cellStyle}>
                          <Chip label={row.gsm} color={getAbnormalityChipColor(row.gsm, ['Normal', 'Good'])} size="small" />
                        </td>
                      </tr>
                      {/* Render extra rows for additional sensors */}
                      {sensors.slice(1).map((sensor, sIdx) => (
                        <tr key={`sensor-${row.id}-${sIdx}`}>
                          <td style={cellStyle}>{sensor.name}</td>
                          <td style={cellStyle}>{sensor.value}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </Box>
        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </DialogContent>
    </Dialog>
  );
}
